// src/web/AboutUs.jsx

import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Trans, useTranslation } from 'react-i18next';
import PageTracker from '../../components/tracking/PageTracker';
import AVIcon from '../../components/icons/AllVoicedIcons';

function AboutUs() {
  const { t } = useTranslation();

  const socials = {
    neus: {
      linkedin: 'https://www.linkedin.com/in/neuscalaf/',
      instagram: 'https://www.instagram.com/neuscalaf',
      orcid: 'https://orcid.org/0009-0003-8286-6528',
    },
    david: {
      orcid: 'https://orcid.org/0000-0002-4909-6686',
    },
    noel: {
      linkedin: 'https://www.linkedin.com/in/noel-warren-350994216/',
    },
    mariona: {
      linkedin: 'https://www.linkedin.com/in/mariona-benito-calaf-481843356/',
      instagram: 'https://www.instagram.com/mariona_benito',
    },
    tim: {
      linkedin: 'https://www.linkedin.com/in/timothy-pomm%C3%A9e-b0735610b/',
      orcid: 'https://orcid.org/0000-0001-7846-7282',
      researchGate: 'https://www.researchgate.net/profile/Timothy-Pommee',
      web: 'https://parlonsvoix.org/',
    },
    ingrid: {
      linkedin: 'https://www.linkedin.com/in/ingridverduyckt/',
      orcid: 'https://orcid.org/0000-0002-1881-3216',
      researchGate: 'https://www.researchgate.net/profile/Ingrid-Verduyckt',
      web: 'https://parlonsvoix.org/',
    },
  };

  const principles = [
    {
      icon: 'listen',
      title: t('about_principle_human_title', 'Human-centered'),
      text: t(
        'about_principle_human_text',
        'Human perception is treated as an active source of evidence, interpretation, and expertise throughout learning, research, and data-generation workflows.'
      ),
    },
    {
      icon: 'research',
      title: t('about_principle_science_title', 'Scientifically grounded'),
      text: t(
        'about_principle_science_text',
        'Tasks, evaluator workflows, data structures, feedback models, and interpretations should remain aligned with methodological, ethical, and governance requirements.'
      ),
    },
    {
      icon: 'sliders',
      title: t('about_principle_modular_title', 'Modular and reusable'),
      text: t(
        'about_principle_modular_text',
        'Shared capabilities can support learning, institutional implementation, research, and annotation without rebuilding every perceptual workflow from scratch.'
      ),
    },
    {
      icon: 'heartHandsShake',
      title: t('about_principle_collaborative_title', 'Collaboratively shaped'),
      text: t(
        'about_principle_collaborative_text',
        'All-Voiced is developed with input and contributions from clinicians, researchers, educators, technologists, designers, institutions, and evaluator communities.'
      ),
    },
  ];

  const relatedPathways = [
    {
      path: '/listening-revolution',
      label: t('about_related_manifesto_label', 'Read the founding manifesto'),
      text: t(
        'about_related_manifesto_text',
        'Explore the founding vision behind All-Voiced and the commitment to human listening.'
      ),
    },
    {
      path: '/collaborations',
      label: t('about_related_collaborations_label', 'Explore collaborations'),
      text: t(
        'about_related_collaborations_text',
        'See how research, educational, and institutional relationships shape the ecosystem.'
      ),
    },
    {
      path: '/services',
      label: t('about_related_services_label', 'Explore All-Voiced services'),
      text: t(
        'about_related_services_text',
        'Understand the public service architecture for learning, institutions, studies, and datasets.'
      ),
    },
    {
      path: '/acknowledgements',
      label: t('about_related_acknowledgements_label', 'View recognitions and contributions'),
      text: t(
        'about_related_acknowledgements_text',
        'Review the contributions, recognitions, and scientific contexts connected to All-Voiced.'
      ),
    },
  ];

  const socialLabel = (name, platform) => `${name} on ${platform}`;

  return (
    <div className="about-page">
      <Helmet defer={false}>
        <title>About All-Voiced | All-Voiced</title>
        <meta
          name="description"
          content="Learn why All-Voiced exists, how its human-centered perceptual infrastructure is being built, and who is shaping its development across education, research, clinical work, and voice technology."
        />
      </Helmet>

      <PageTracker pageName="AboutUs" />

      {/* 🌱 HERO SECTION */}
      <section className="about-hero">
        <div className="about-content">
          <div className="about-text">
            <h1>{t('about_title', 'About All-Voiced')}</h1>

            <p>
              <Trans i18nKey="about_intro">
                <strong>All-Voiced</strong> is a human-centered infrastructure for
                developing, studying, and structuring auditory-perceptual voice
                evaluation.
              </Trans>
            </p>

            <p>
              {t(
                'about_intro_2',
                'It helps transform human listening into structured, comparable, and reusable evidence — supporting learning, clinical and research studies, dataset annotation, and voice technology evaluation.'
              )}
            </p>

            <div className="about-hero-actions web-cta-row">
              <Link className="web-button web-button--primary" to="/listening-revolution">
                {t('about_manifesto_cta', 'Read the manifesto')}
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* WHY ALL-VOICED EXISTS */}
      <section className="about-section about-purpose">
        <div className="about-section-content">
          <span className="about-label">
            {t('about_why_label', 'Why All-Voiced exists')}
          </span>

          <h2>{t('about_why_title', 'Human listening deserves infrastructure')}</h2>

          <p>
            {t(
              'about_why_text_1',
              'Auditory-perceptual voice evaluation is central to how people learn, assess, study, and work with voice. Yet perceptual judgments are often collected through isolated exercises, local protocols, or one-off research workflows that are difficult to compare, reuse, or connect.'
            )}
          </p>

          <p>
            {t(
              'about_why_text_2',
              'All-Voiced was created to give human listening a stronger infrastructural role — supporting structured practice, evaluator workflows, research studies, and perceptual data generation without reducing human judgment to a single interchangeable label.'
            )}
          </p>
        </div>
      </section>

      {/* HOW ALL-VOICED IS BEING BUILT */}
      <section className="about-section about-principles">
        <div className="about-section-content">
          <span className="about-label">
            {t('about_build_label', 'How it is being built')}
          </span>

          <h2>{t('about_build_title', 'Principles guiding its development')}</h2>

          <div className="about-principles-grid">
            {principles.map((principle) => (
              <article className="about-principle-card" key={principle.title}>
                <AVIcon icon={principle.icon} size={28} color="#209385" />
                <h3>{principle.title}</h3>
                <p>{principle.text}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* 🤝 PEOPLE SECTION */}
      <section className="about-advisors">
        <div className="advisors-content">
          <h2>{t('about_people_title', 'The people shaping All-Voiced')}</h2>

          <p className="advisors-intro">
            {t(
              'about_people_intro',
              'All-Voiced is led by Neus Calaf and shaped by a growing network of people contributing scientific, clinical, educational, technological, and design expertise.'
            )}
          </p>

          <div className="advisors-grid visual">
            {/* Neus */}
            <div className="advisor-card founder-card">
              <div className="advisor-photo">
                <img
                  src="/images/about/neus-calaf.png"
                  alt={t('about_photo_alt', 'Neus Calaf')}
                />
              </div>

              <h3>Neus Calaf, PhD, SLP</h3>
              <p>{t('founder_project_lead', 'Founder & Project Lead')}</p>

              <div className="advisor-socials">
                <a
                  href={socials.neus.linkedin}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={socialLabel('Neus Calaf', 'LinkedIn')}
                >
                  <AVIcon icon="linkedin" size={24} />
                </a>
                <a
                  href={socials.neus.instagram}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={socialLabel('Neus Calaf', 'Instagram')}
                >
                  <AVIcon icon="instagram" size={24} />
                </a>
                <a
                  href={socials.neus.orcid}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={socialLabel('Neus Calaf', 'ORCID')}
                >
                  <AVIcon icon="orcid" size={24} />
                </a>
              </div>
            </div>

            {/* Mariona */}
            <div className="advisor-card">
              <div className="advisor-photo">
                <img src="/images/about/mariona-benito.png" alt="Mariona Benito" />
              </div>

              <h3>Mariona Benito</h3>
              <p>{t('visual_identity_and_design_advisor', 'Visual Identity & Design Advisor')}</p>

              <div className="advisor-socials">
                <a
                  href={socials.mariona.linkedin}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={socialLabel('Mariona Benito', 'LinkedIn')}
                >
                  <AVIcon icon="linkedin" size={24} />
                </a>
                <a
                  href={socials.mariona.instagram}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={socialLabel('Mariona Benito', 'Instagram')}
                >
                  <AVIcon icon="instagram" size={24} />
                </a>
              </div>
            </div>

            {/* David */}
            <div className="advisor-card">
              <div className="advisor-photo">
                <img src="/images/about/david-garcia-quintana.png" alt="David Garcia-Quintana" />
              </div>

              <h3>David Garcia-Quintana, PhD</h3>
              <p>{t('scientific_research_advisor', 'Scientific Research Advisor')}</p>

              <div className="advisor-socials">
                <a
                  href={socials.david.orcid}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={socialLabel('David Garcia-Quintana', 'ORCID')}
                >
                  <AVIcon icon="orcid" size={24} />
                </a>
              </div>
            </div>

            {/* Tim */}
            <div className="advisor-card">
              <div className="advisor-photo">
                <img src="/images/about/tim-pommee.png" alt="Timothy Pommée" />
              </div>

              <h3>Timothy Pommée, PhD</h3>
              <p>{t('research_collaborator', 'Research Collaborator')}</p>

              <div className="advisor-socials">
                <a
                  href={socials.tim.linkedin}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={socialLabel('Timothy Pommée', 'LinkedIn')}
                >
                  <AVIcon icon="linkedin" size={24} />
                </a>
                <a
                  href={socials.tim.orcid}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={socialLabel('Timothy Pommée', 'ORCID')}
                >
                  <AVIcon icon="orcid" size={24} />
                </a>
                <a
                  href={socials.tim.researchGate}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={socialLabel('Timothy Pommée', 'ResearchGate')}
                >
                  <AVIcon icon="researchGate" size={21} />
                </a>
                <a
                  href={socials.tim.web}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={socialLabel('Timothy Pommée', 'website')}
                >
                  <AVIcon icon="globe" size={24} />
                </a>
              </div>
            </div>

            {/* Ingrid */}
            <div className="advisor-card">
              <div className="advisor-photo">
                <img src="/images/about/ingrid-verduyckt.png" alt="Ingrid Verduyckt" />
              </div>

              <h3>Ingrid Verduyckt, PhD</h3>
              <p>{t('research_collaborator', 'Research Collaborator')}</p>

              <div className="advisor-socials">
                <a
                  href={socials.ingrid.linkedin}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={socialLabel('Ingrid Verduyckt', 'LinkedIn')}
                >
                  <AVIcon icon="linkedin" size={24} />
                </a>
                <a
                  href={socials.ingrid.orcid}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={socialLabel('Ingrid Verduyckt', 'ORCID')}
                >
                  <AVIcon icon="orcid" size={24} />
                </a>
                <a
                  href={socials.ingrid.researchGate}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={socialLabel('Ingrid Verduyckt', 'ResearchGate')}
                >
                  <AVIcon icon="researchGate" size={21} />
                </a>
                <a
                  href={socials.ingrid.web}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={socialLabel('Ingrid Verduyckt', 'website')}
                >
                  <AVIcon icon="globe" size={24} />
                </a>
              </div>
            </div>

            {/* Noel */}
            <div className="advisor-card">
              <div className="advisor-photo">
                <img src="/images/about/noel-warren.png" alt="Noel Warren" />
              </div>

              <h3>Noel Warren</h3>
              <p>{t('technology_advisor', 'Technology Advisor')}</p>

              <div className="advisor-socials">
                <a
                  href={socials.noel.linkedin}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={socialLabel('Noel Warren', 'LinkedIn')}
                >
                  <AVIcon icon="linkedin" size={24} />
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="about-related" aria-labelledby="about-related-title">
        <div className="about-section-content">
          <h2 id="about-related-title">
            {t('about_related_title', 'Explore the wider All-Voiced ecosystem')}
          </h2>

          <div className="about-related-grid">
            {relatedPathways.map((pathway) => (
              <Link className="about-related-card" to={pathway.path} key={pathway.path}>
                <h3>{pathway.label}</h3>
                <p>{pathway.text}</p>
              </Link>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}

export default AboutUs;
